import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { fetchMyOrders } from "../lib/api";
import { fmtARS, STATUS_LABEL, STATUS_DOT } from "../lib/utils-app";
import { toast } from "sonner";
import { Gem, ArrowRight, RefreshCw, Package } from "lucide-react";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(true);

  const load = async () => {
    setBusy(true);
    try {
      setOrders(await fetchMyOrders());
    } catch {
      toast.error("No se pudieron cargar tus pedidos");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!loading && user) load();
  }, [loading, user]);

  if (loading) return null;
  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="font-display text-4xl">Iniciá sesión para ver tus pedidos</h1>
        <Link to="/" className="btn-primary inline-block mt-6 px-5 py-3 rounded-md">Volver al inicio</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <div className="text-xs tracking-[0.3em] text-[#ff4500]">MI CUENTA</div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-wider">Mis pedidos</h1>
            <p className="text-neutral-400 mt-1">Hola, {user.name || user.email}. Acá vas a ver el estado de todas tus recargas.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} data-testid="refresh-orders-btn" className="px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 flex items-center gap-2 text-sm">
              <RefreshCw className="w-4 h-4" /> Actualizar
            </button>
            <Link to="/" data-testid="new-recharge-link" className="btn-primary px-4 py-2 rounded-md flex items-center gap-2 text-sm">
              Nueva recarga <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {busy ? (
          <div className="text-neutral-400">Cargando...</div>
        ) : orders.length === 0 ? (
          <div className="rounded-xl border border-white/10 p-10 text-center bg-[#0d0d0d]">
            <Package className="w-10 h-10 mx-auto text-[#ff4500] mb-3" />
            <div className="font-display text-3xl">Aún no tenés pedidos</div>
            <p className="text-neutral-400 mt-1">Realizá tu primera recarga y aparecerá acá.</p>
            <Link to="/" className="btn-primary inline-block mt-5 px-5 py-3 rounded-md">Recargar diamantes</Link>
          </div>
        ) : (
          <div className="grid gap-3">
            {orders.map((o) => (
              <div
                key={o.order_id}
                data-testid={`order-row-${o.order_id}`}
                className="rounded-lg border border-white/10 bg-[#0d0d0d] p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4"
              >
                <div className="w-11 h-11 rounded-md bg-[#0f0f0f] border border-[#ff4500]/40 grid place-items-center shrink-0">
                  <Gem className="w-5 h-5 text-[#ff4500]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-2xl tracking-wider">
                    {o.diamonds ? `${o.diamonds.toLocaleString("es-AR")} DIAMANTES` : `${o.quantity || 1} × ${(o.product_name || "Pedido").toUpperCase()}`}
                  </div>
                  <div className="text-xs text-neutral-500 truncate">
                    #{o.order_id} · Player ID: <span className="text-neutral-300 font-mono">{o.player_id}</span>
                    {o.player_nickname ? <> · {o.player_nickname}</> : null}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500">Total</div>
                  <div className="font-semibold">{fmtARS(o.amount_ars)}</div>
                </div>
                <div className="sm:min-w-[160px] flex items-center gap-2 justify-end">
                  <span className={`inline-block w-2 h-2 rounded-full ${STATUS_DOT[o.status] || "bg-neutral-500"}`} />
                  <span className={`text-sm status-${o.status}`}>{STATUS_LABEL[o.status] || o.status}</span>
                </div>
                {o.status === "pending_payment" && o.checkout_url && (
                  <a
                    href={o.checkout_url}
                    data-testid={`resume-checkout-${o.order_id}`}
                    className="btn-primary px-4 py-2 rounded-md text-sm text-center"
                  >
                    Continuar pago
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
